<template>
    <article class="apartment-list-item">
        <div class="apartment-list-item__content">
            <div
                v-if="!!$slots.type || pending"
                class="apartment-list-item__type"
            >
                <template v-if="pending">
                    <Skeletor
                        :shimmer="false"
                        width="40%"
                    />
                </template>
                <template v-else>
                    <slot name="type" />
                </template>
            </div>

            <div
                v-if="!!$slots.description || pending"
                class="apartment-list-item__description"
            >
                <template v-if="pending">
                    <h3>
                        <Skeletor
                            :shimmer="false"
                            width="80%"
                        />
                        <Skeletor
                            :shimmer="false"
                        />
                        <Skeletor
                            :shimmer="false"
                            width="35%"
                        />
                    </h3>
                </template>
                <template v-else>
                    <slot name="description" />
                </template>
            </div>

            <div
                v-if="!!$slots.amenities || pending"
                class="apartment-list-item__amenities"
            >
                <slot name="amenities" />
            </div>

            <div class="apartment-list-item__footer">
                <template v-if="pending">
                    <Skeletor
                        width="50%"
                        :shimmer="false"
                    />
                </template>
                <template v-else>
                    <BaseButton
                        v-if="bookable"
                        :element="NuxtLink"
                        class="base-button base-button-large"
                        @click="$emit('open-booking-engine')"
                    >
                        <slot name="cta" />
                    </BaseButton>

                    <BaseArrowLink
                        v-if="link"
                        :to="link"
                        label-position="right"
                    >
                        <slot name="link" />
                    </BaseArrowLink>
                </template>
            </div>
        </div>

        <BaseSlider
            v-if="images?.length || pending"
            class="apartment-list-item__slider"
            :items="images"
            :controls-enabled="enableControls"
            :padding="'image-width'"
            @mouseenter="enableControls = true"
        >
            <template
                v-for="(asset, index) in images"
                #[`slide-${index}`]
                :key="`slide-${index}`"
            >
                <Skeletor
                    v-if="pending"
                    width="100%"
                    height="100%"
                    :shimmer="false"
                />
                <BaseVideo
                    v-else-if="getMediaType(asset) === 'mp4' || getMediaType(asset) === 'webm'"
                    v-motion
                    :initial="{ 
                        opacity: 0, 
                        y: 0, 
                        scale: 1.05 
                    }"
                    :visible="{ 
                        opacity: 1, 
                        y: 0, 
                        scale: 1, 
                        transition: {
                            duration: 1000,
                            ease: '0.4, 0, 0.2, 1',
                            delay: 0
                        }
                    }"
                    :mp4="getMediaType(asset) === 'mp4' ? asset.url : null"
                    :webm="getMediaType(asset) === 'webm' ? asset.url : null"
                />
                <BaseImage
                    v-else
                    class="apartment-list-item__image"
                    :image="asset"
                    :width="asset.width"
                    :height="asset.height"
                    draggable="false"
                    sizes="(min-width: 992px) 50vw, (min-width: 642px) 600px, 93.5vw"
                />
            </template>
        </BaseSlider>
    </article>
</template>

<script setup>
import BaseArrowLink from '~/components/BaseArrowLink.vue';
import BaseButton from '~/components/BaseButton.vue';
import BaseImage from '~/components/BaseImage.vue';
import BaseSlider from '~/components/BaseSlider.vue';

const enableControls = ref(false);

defineEmits(['open-booking-engine']);

const props = defineProps({
    apartment: {
        type: Object,
        required: true,
        default: null
    },
    images: {
        type: Object,
        required: false,
        default: null
    },
    link: {
        type: String,
        default: null
    },
    pending: {
        type: Boolean,
        default: false
    },
    bookable: {
        type: Boolean,
        default: false
    }
});

defineOptions({
    name: 'ApartmentListItem',
});

const NuxtLink = resolveComponent('NuxtLink');

const showBookingLink = computed(() => {
    return props.apartment?.property?.bookingEngineDomain?.length || props.apartment?.property?.bookingEngineId?.length;
});

function getMediaType(media) {
    if (media?.url?.includes('.mp4')) {
        return 'mp4';
    }
    if (media?.url?.includes('.webm')) {
        return 'webm';
    }

    return 'image';
}
</script>

<style lang="less" src="./ApartmentListItem.less" />
